import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import './i18n'; // i18n yapılandırmasını import et

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

// Components
import RoleBasedRoute from './components/RoleBasedRoute';

// Auth Pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard';

// Region Pages
import RegionList from './pages/regions/RegionList';
import RegionCreate from './pages/regions/RegionCreate';
import RegionEdit from './pages/regions/RegionEdit';

// Agency Pages
import AgencyList from './pages/agencies/AgencyList';
import AgencyCreate from './pages/agencies/AgencyCreate';
import AgencyEdit from './pages/agencies/AgencyEdit';
import AgencyDetail from './pages/agencies/AgencyDetail';

// Carrier Pages
import CarrierList from './pages/carriers/CarrierList';
import CarrierCreate from './pages/carriers/CarrierCreate';
import CarrierEdit from './pages/carriers/CarrierEdit';
import CarrierDetail from './pages/carriers/CarrierDetail';

// Vehicle Pages
import VehicleList from './pages/vehicles/VehicleList';
import VehicleCreate from './pages/vehicles/VehicleCreate';
import VehicleEdit from './pages/vehicles/VehicleEdit';
import VehicleDetail from './pages/vehicles/VehicleDetail';

// Price Pages
import PriceList from './pages/prices/PriceList';
import PriceCreate from './pages/prices/PriceCreate';
import PriceEdit from './pages/prices/PriceEdit';
import PriceBulkUpdate from './pages/prices/PriceBulkUpdate';

// Reservation Pages
import ReservationList from './pages/reservations/ReservationList';
import ReservationCreate from './pages/reservations/ReservationCreate';
import ReservationDetail from './pages/reservations/ReservationDetail';
import ReservationEdit from './pages/reservations/ReservationEdit';


// Transfer Pages
import TransferList from './pages/operations/Operation';
import AgencyTransferList from './pages/agency-transfers/Transfers';
import CarrierTransfersList from './pages/carrier-transfers/Transfers';



// Finance Pages
import Finance from './pages/finance/Finance';
import CarrierFinanceDetail from './pages/finance/CarrierFinanceDetail';

// User Pages
import UserManagement from './pages/users/UserManagement';

// Report Pages
import Reports from './pages/reports/Reports';

// Not Found Page
import NotFound from './pages/NotFound';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    </div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirects if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    </div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <Routes>
      {/* Auth Routes */}
      <Route path="/" element={<PublicRoute><AuthLayout /></PublicRoute>}>
        <Route index element={<Navigate to="/login" replace />} />
        <Route path="login" element={<Login />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
      </Route>

      {/* Protected Routes */}
      <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<Dashboard />} />

        {/* Region Routes - Sadece sistem kullanıcıları */}
        <Route path="regions" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><RegionList /></RoleBasedRoute>} />
        <Route path="regions/create" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><RegionCreate /></RoleBasedRoute>} />
        <Route path="regions/:id/edit" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><RegionEdit /></RoleBasedRoute>} />

        {/* Agency Routes - Sadece sistem kullanıcıları */}
        <Route path="agencies" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><AgencyList /></RoleBasedRoute>} />
        <Route path="agencies/create" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><AgencyCreate /></RoleBasedRoute>} />
        <Route path="agencies/:id/edit" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><AgencyEdit /></RoleBasedRoute>} />
        <Route path="agencies/:id" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><AgencyDetail /></RoleBasedRoute>} />

        {/* Carrier Routes - Sadece sistem kullanıcıları */}
        <Route path="carriers" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><CarrierList /></RoleBasedRoute>} />
        <Route path="carriers/create" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><CarrierCreate /></RoleBasedRoute>} />
        <Route path="carriers/:id/edit" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><CarrierEdit /></RoleBasedRoute>} />
        <Route path="carriers/:id" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><CarrierDetail /></RoleBasedRoute>} />

        {/* Vehicle Routes - Sadece sistem kullanıcıları */}
        <Route path="vehicles" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><VehicleList /></RoleBasedRoute>} />
        <Route path="vehicles/create" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><VehicleCreate /></RoleBasedRoute>} />
        <Route path="vehicles/:id/edit" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><VehicleEdit /></RoleBasedRoute>} />
        <Route path="vehicles/:id" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><VehicleDetail /></RoleBasedRoute>} />

        {/* Price Routes - Sadece sistem kullanıcıları */}
        <Route path="prices" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><PriceList /></RoleBasedRoute>} />
        <Route path="prices/create" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><PriceCreate /></RoleBasedRoute>} />
        <Route path="prices/bulk" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><PriceBulkUpdate /></RoleBasedRoute>} />
        <Route path="prices/:id/edit" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><PriceEdit /></RoleBasedRoute>} />

        {/* Reservation Routes - Sistem kullanıcıları ve acente kullanıcıları */}
        <Route path="reservations" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin', 'agency_user']}><ReservationList /></RoleBasedRoute>} />
        <Route path="reservations/create" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin', 'agency_user']}><ReservationCreate /></RoleBasedRoute>} />
        <Route path="reservations/:id" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin', 'agency_user', 'carrier_user']}><ReservationDetail /></RoleBasedRoute>} />
        <Route path="reservations/:id/edit" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin', 'agency_user']}><ReservationEdit /></RoleBasedRoute>} />

        {/* Operation Routes - Sadece sistem kullanıcıları */}
        <Route path="operations" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><TransferList /></RoleBasedRoute>} />
        <Route path="agency-transfers" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin', 'agency_user']}><AgencyTransferList /></RoleBasedRoute>} />
        <Route path="carrier-transfers" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin', 'carrier_user']}><CarrierTransfersList /></RoleBasedRoute>} />

        {/* Finance Routes - Sistem kullanıcıları ve taşıyıcı kullanıcıları */}
        <Route path="finance" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin', 'carrier_user']}><Finance /></RoleBasedRoute>} />
        <Route path="finance/carrier/:carrierId" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin', 'carrier_user']}><CarrierFinanceDetail /></RoleBasedRoute>} />

        {/* User Routes - Sadece sistem kullanıcıları */}
        <Route path="user-management" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><UserManagement /></RoleBasedRoute>} />
        
        {/* Report Routes - Sadece sistem kullanıcıları */}
        <Route path="reports" element={<RoleBasedRoute allowedRoles={['admin', 'super_admin']}><Reports /></RoleBasedRoute>} />
      </Route>

      {/* Not Found Route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
